import { motion, AnimatePresence } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { AudienceMark } from '@/components/AudienceMarks'

type Audience = 'community' | 'academy' | 'venue'

type AudienceConfig = {
  key: Audience
  label: string
  tagline: string
  url: string
  imageSrc: string
  imageAlt: string
}

const audiences: AudienceConfig[] = [
  {
    key: 'community',
    label: 'Community',
    tagline: 'Grow your community, not your workload.',
    url: 'statbox.com/portal',
    imageSrc: '/Dashboard Community PLatform.png',
    imageAlt: 'Statbox community manager dashboard overview',
  },
  {
    key: 'academy',
    label: 'Academy',
    tagline: 'Train smarter. Show parents the progress.',
    url: 'app.statbox.co/academy',
    imageSrc: '/Dashboard Academy Platform.png',
    imageAlt: 'Statbox academy dashboard overview',
  },
  {
    key: 'venue',
    label: 'Venue',
    tagline: 'A smart court is a fully-booked court.',
    url: 'app.statbox.co/venue',
    imageSrc: '/Dashboard venue onwer.png',
    imageAlt: 'Statbox venue owner dashboard overview',
  },
]

const ROTATE_MS = 4000
const IMAGE_ASPECT = '2872 / 1556'
const CROSSFADE = { duration: 0.55, ease: [0.45, 0, 0.55, 1] as const }

function encodeAudienceSrc(src: string) {
  return encodeURI(src)
}

/** Preload only the active slide (+ next on desktop) when the section is near the viewport. */
function usePreloadAudienceImages(activeIndex: number, enabled: boolean, preloadAll: boolean) {
  useEffect(() => {
    if (!enabled) return

    const indices = preloadAll
      ? audiences.map((_, i) => i)
      : [activeIndex, (activeIndex + 1) % audiences.length]

    indices.forEach((index) => {
      const img = new Image()
      img.decoding = 'async'
      img.src = encodeAudienceSrc(audiences[index].imageSrc)
    })
  }, [activeIndex, enabled, preloadAll])
}

function useLoadedDashboardImages() {
  const loadedRef = useRef<Set<string>>(new Set())
  const [loadedTick, setLoadedTick] = useState(0)

  const markLoaded = useCallback((src: string) => {
    if (loadedRef.current.has(src)) return
    loadedRef.current.add(src)
    setLoadedTick((n) => n + 1)
  }, [])

  const isLoaded = useCallback(
    (src: string) => loadedRef.current.has(src),
    [loadedTick],
  )

  return { markLoaded, isLoaded }
}

const DashboardImageSkeleton = () => (
  <div
    aria-hidden
    className="absolute inset-0 bg-gradient-to-br from-[#e8eaef] via-[#f4f5f7] to-[#dfe3ea] animate-pulse"
  >
    <div className="absolute inset-x-0 top-0 h-8 bg-white/70" />
    <div className="absolute left-4 top-14 h-3 w-24 rounded-full bg-white/80" />
    <div className="absolute left-4 top-20 h-3 w-32 rounded-full bg-white/60" />
    <div className="absolute inset-x-4 bottom-4 top-28 rounded-lg bg-white/50" />
  </div>
)

/* ---------------- Browser frame ---------------- */

const BrowserFrame = ({
  audiences,
  activeIndex,
  isLoaded,
  markLoaded,
}: {
  audiences: AudienceConfig[]
  activeIndex: number
  isLoaded: (src: string) => boolean
  markLoaded: (src: string) => void
}) => {
  const active = audiences[activeIndex]
  const activeSrc = encodeAudienceSrc(active.imageSrc)
  const showSkeleton = !isLoaded(activeSrc)

  useEffect(() => {
    const img = new Image()
    img.src = activeSrc
    if (img.complete) {
      markLoaded(activeSrc)
    }
  }, [activeSrc, markLoaded])

  return (
    <div className="relative w-full">
      {/* Teal glow — lighter on mobile (large blurs are expensive) */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-4 sm:-inset-10 md:blur-[80px] blur-[40px]"
        style={{
          background:
            'radial-gradient(closest-side, rgba(113,205,217,0.2) 0%, transparent 70%)',
        }}
      />
      <div className="relative w-full rounded-[12px] border border-white/[0.08] bg-[#0c0c0f] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
        {/* Title bar */}
        <div className="relative z-10 flex h-9 items-center gap-2 border-b border-white/[0.06] bg-[#141418] px-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex flex-1 justify-center overflow-hidden">
            <div className="relative h-6 min-w-[140px] max-w-[220px] flex-1">
              <AnimatePresence initial={false}>
                <motion.div
                  key={active.url}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={CROSSFADE}
                  className="absolute inset-0 flex items-center justify-center rounded-md border border-white/[0.05] bg-white/[0.04] px-3 text-[11px] font-mono text-white/45"
                >
                  {active.url}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Dashboard screenshots — skeleton reserves space while PNG loads */}
        <div
          className="relative w-full overflow-hidden bg-[#f4f5f7]"
          style={{ aspectRatio: IMAGE_ASPECT }}
        >
          {showSkeleton ? <DashboardImageSkeleton /> : null}
          <AnimatePresence initial={false}>
            <motion.img
              key={active.key}
              src={activeSrc}
              alt={active.imageAlt}
              loading={activeIndex === 0 ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={activeIndex === 0 ? 'high' : 'auto'}
              draggable={false}
              onLoad={() => markLoaded(activeSrc)}
              initial={{ opacity: isLoaded(activeSrc) ? 1 : 0 }}
              animate={{ opacity: isLoaded(activeSrc) ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={CROSSFADE}
              className="absolute inset-0 h-full w-full object-cover object-left-top"
            />
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

const DashboardVisual = ({
  audiences,
  activeIndex,
  onHoverChange,
  isLoaded,
  markLoaded,
}: {
  audiences: AudienceConfig[]
  activeIndex: number
  onHoverChange?: (hovered: boolean) => void
  isLoaded: (src: string) => boolean
  markLoaded: (src: string) => void
}) => (
  <div
    className="w-full"
    onMouseEnter={() => onHoverChange?.(true)}
    onMouseLeave={() => onHoverChange?.(false)}
  >
    <BrowserFrame
      audiences={audiences}
      activeIndex={activeIndex}
      isLoaded={isLoaded}
      markLoaded={markLoaded}
    />
  </div>
)

/* ---------------- Audience selector ---------------- */

const AudienceCard = ({
  config,
  active,
  onSelect,
}: {
  config: AudienceConfig
  active: boolean
  onSelect: () => void
}) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'group relative h-full w-full overflow-hidden rounded-[16px] border text-left transition-all duration-300 ease-out',
        active
          ? 'border-[#71cdd9]/55 bg-[#71cdd9]/22 shadow-[0_16px_40px_-18px_rgba(113,205,217,0.7),inset_0_1px_0_rgba(255,255,255,0.18)]'
          : 'border-white/[0.07] bg-white/[0.03] hover:border-white/[0.14] hover:bg-white/[0.055]',
      ].join(' ')}
    >
      {active && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#c8f4f8] to-transparent"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(181,234,242,0.22),transparent_58%)]"
          />
        </>
      )}

      <div className="relative flex h-full items-center gap-3.5 px-4 py-4">
        {/* Icon tile: gradient rim + glass face + glow */}
        <div className="relative h-10 w-10 shrink-0">
          {active && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{
                background: 'rgba(165,231,240,0.55)',
                filter: 'blur(12px)',
                transform: 'scale(1.2)',
              }}
            />
          )}
          <div
            className="relative h-full w-full rounded-xl p-[1px] transition-all duration-300"
            style={{
              background: active
                ? 'linear-gradient(160deg, #e7fbff 0%, #71cdd9 45%, #4db3c0 100%)'
                : 'linear-gradient(160deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)',
            }}
          >
            <div
              className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-[11px] transition-all duration-300"
              style={{
                background: active
                  ? 'linear-gradient(180deg, #8fe0ec 0%, #71cdd9 100%)'
                  : 'linear-gradient(180deg, #1a1b21 0%, #0e0f13 100%)',
                boxShadow: active
                  ? 'inset 0 1px 0 rgba(255,255,255,0.45)'
                  : 'inset 0 1px 0 rgba(255,255,255,0.1)',
              }}
            >
              <AudienceMark
                kind={config.key}
                className={[
                  'relative h-5 w-5 transition-all duration-300',
                  active
                    ? 'text-[#062126]'
                    : 'text-white/45 group-hover:text-white/70',
                ].join(' ')}
              />
            </div>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div
            className={[
              'text-[15px] font-bold tracking-tight leading-none transition-colors duration-300',
              active ? 'text-white' : 'text-white/50 group-hover:text-white/75',
            ].join(' ')}
          >
            {config.label}
          </div>
          <p
            className={[
              'mt-1.5 min-h-[2.4em] text-[12.5px] leading-snug transition-colors duration-300',
              active ? 'text-white/88' : 'text-white/35 group-hover:text-white/50',
            ].join(' ')}
            style={{ fontFamily: '"Open Sans", system-ui, sans-serif' }}
          >
            {config.tagline}
          </p>
        </div>

        <ChevronRight
          className={[
            'h-4 w-4 shrink-0 transition-all duration-300',
            active
              ? 'text-white opacity-100'
              : 'text-white/20 opacity-40 group-hover:opacity-70',
          ].join(' ')}
          aria-hidden
        />
      </div>
    </button>
  )
}

const AudienceTabs = ({
  audiences,
  activeIndex,
  onSelect,
}: {
  audiences: AudienceConfig[]
  activeIndex: number
  onSelect: (index: number) => void
}) => (
  <div className="relative rounded-2xl border border-white/[0.08] bg-[#0a0b0e]/85 p-1.5 backdrop-blur-xl">
    <div className="grid grid-cols-3 gap-1">
      {audiences.map((config, index) => {
        const active = activeIndex === index
        return (
          <button
            key={config.key}
            type="button"
            onClick={() => onSelect(index)}
            className={[
              'relative rounded-xl px-2 py-3 text-center text-[11px] font-semibold transition-colors duration-200 sm:text-sm',
              active ? 'text-black' : 'text-white/50 hover:text-white/80',
            ].join(' ')}
          >
            {active && (
              <motion.div
                layoutId="features-audience-pill"
                className="absolute inset-0 rounded-xl"
                style={{
                  background: 'linear-gradient(135deg, #71cdd9 0%, #b5eaf2 100%)',
                  boxShadow:
                    'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 24px rgba(113,205,217,0.30)',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10">{config.label}</span>
          </button>
        )
      })}
    </div>
  </div>
)

/* ---------------- Section ---------------- */

export const FeaturesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [rotationReady, setRotationReady] = useState(false)
  const [imagesEnabled, setImagesEnabled] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const rotateTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const active = audiences[activeIndex]
  const { markLoaded, isLoaded } = useLoadedDashboardImages()
  const firstImageSrc = encodeAudienceSrc(audiences[0].imageSrc)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImagesEnabled(true)
          observer.disconnect()
        }
      },
      { rootMargin: '240px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  usePreloadAudienceImages(activeIndex, imagesEnabled, rotationReady)

  useEffect(() => {
    if (!imagesEnabled) return

    const img = new Image()
    const finish = () => {
      markLoaded(firstImageSrc)
      setRotationReady(true)
    }
    img.onload = finish
    img.onerror = finish
    img.decoding = 'async'
    img.src = firstImageSrc
    if (img.complete) finish()
  }, [firstImageSrc, markLoaded, imagesEnabled])

  const startRotation = useCallback(() => {
    if (rotateTimer.current) clearInterval(rotateTimer.current)
    rotateTimer.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % audiences.length)
    }, ROTATE_MS)
  }, [])

  useEffect(() => {
    if (paused || !rotationReady) {
      if (rotateTimer.current) clearInterval(rotateTimer.current)
      return undefined
    }
    startRotation()
    return () => {
      if (rotateTimer.current) clearInterval(rotateTimer.current)
    }
  }, [paused, rotationReady, startRotation])

  const selectAudience = (index: number) => {
    setActiveIndex(index)
    if (!paused) startRotation()
  }

  return (
    <section
      ref={sectionRef}
      id="features"
      className="relative bg-black py-12 sm:py-20 overflow-hidden"
    >
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 1, y: 0 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.15] text-white">
            Built for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-emerald-200 to-cyan-300">
              how you operate.
            </span>
          </h2>
          <p
            className="mt-4 text-base sm:text-lg text-white/60 max-w-xl mx-auto"
            style={{ fontFamily: '"Open Sans", system-ui, sans-serif' }}
          >
            Three audiences. One Statbox.
          </p>
        </motion.div>

        {/* Two-column layout (desktop) */}
        <div className="mt-12 sm:mt-16 hidden md:grid md:grid-cols-[minmax(240px,30%)_minmax(0,1fr)] gap-6 lg:gap-8 items-center">
          {/* LEFT: audience cards — gradient hairline frame */}
          <div
            className="rounded-[22px] p-[1px] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.8)]"
            style={{
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 35%, rgba(113,205,217,0.10) 100%)',
            }}
          >
            <div className="relative grid auto-rows-fr gap-2 overflow-hidden rounded-[21px] bg-[#0a0b0e]/90 p-2 backdrop-blur-sm">
              <div
                aria-hidden
                className="pointer-events-none absolute -left-16 -top-20 h-52 w-52 rounded-full bg-[#71cdd9]/[0.08] blur-3xl"
              />
              {audiences.map((a, i) => (
                <AudienceCard
                  key={a.key}
                  config={a}
                  active={activeIndex === i}
                  onSelect={() => selectAudience(i)}
                />
              ))}
            </div>
          </div>

          {/* RIGHT: crossfading visual */}
          <div className="sticky top-24 self-start min-w-0">
            <DashboardVisual
              audiences={audiences}
              activeIndex={activeIndex}
              onHoverChange={setPaused}
              isLoaded={isLoaded}
              markLoaded={markLoaded}
            />
          </div>
        </div>

        {/* Mobile: tabs + visual */}
        <div className="mt-10 md:hidden space-y-5">
          <AudienceTabs
            audiences={audiences}
            activeIndex={activeIndex}
            onSelect={selectAudience}
          />

          <div className="relative min-h-[3rem]">
            <AnimatePresence initial={false}>
              <motion.p
                key={active.key}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={CROSSFADE}
                className="absolute inset-x-0 top-0 text-center text-sm leading-relaxed text-white/65 px-2"
                style={{ fontFamily: '"Open Sans", system-ui, sans-serif' }}
              >
                {active.tagline}
              </motion.p>
            </AnimatePresence>
          </div>

          <DashboardVisual
            audiences={audiences}
            activeIndex={activeIndex}
            isLoaded={isLoaded}
            markLoaded={markLoaded}
          />
        </div>
      </div>
    </section>
  )
}
